import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const SESSION_IDLE_TIMEOUT_MS = Number(process.env.REACT_APP_SESSION_IDLE_TIMEOUT_MS || 30 * 60 * 1000);
const SESSION_ABSOLUTE_TIMEOUT_MS = Number(process.env.REACT_APP_SESSION_ABSOLUTE_TIMEOUT_MS || 12 * 60 * 60 * 1000);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null); // { method, token }
  const idleTimerRef = useRef(null);
  const absoluteTimerRef = useRef(null);

  const clearSessionTimers = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (absoluteTimerRef.current) {
      window.clearTimeout(absoluteTimerRef.current);
      absoluteTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    clearSessionTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('token_expires');
    localStorage.removeItem('session_started_at');
    localStorage.removeItem('session_last_activity_at');
    localStorage.removeItem('menu_cache');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  }, [clearSessionTimers]);

  const expireSession = useCallback(() => {
    logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, [logout]);

  const resetIdleTimer = useCallback(() => {
    if (!localStorage.getItem('token')) return;
    localStorage.setItem('session_last_activity_at', String(Date.now()));
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = window.setTimeout(() => {
      expireSession();
    }, SESSION_IDLE_TIMEOUT_MS);
  }, [expireSession]);

  const scheduleAbsoluteTimer = useCallback(() => {
    if (absoluteTimerRef.current) {
      window.clearTimeout(absoluteTimerRef.current);
    }
    const startedAt = Number(localStorage.getItem('session_started_at') || Date.now());
    const remaining = startedAt + SESSION_ABSOLUTE_TIMEOUT_MS - Date.now();
    if (remaining <= 0) {
      expireSession();
      return;
    }
    absoluteTimerRef.current = window.setTimeout(() => {
      expireSession();
    }, remaining);
  }, [expireSession]);

  const checkAuth = useCallback(async () => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '' : '';
      console.log('checkAuth: Making request to:', `${baseURL}/api/auth/profile`);
      console.log('checkAuth: Authorization header:', axios.defaults.headers.common['Authorization']);
      
      const response = await axios.get(`${baseURL}/api/auth/profile`);
      console.log('Auth checkAuth response:', response.data);
      setUser(response.data.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      console.error('Auth check error response:', error.response?.data);
      console.error('Auth check error status:', error.response?.status);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // 检查token是否过期
  const isTokenExpired = useCallback(() => {
    const expiresAt = localStorage.getItem('token_expires');
    if (!expiresAt) return true;
    return new Date() >= new Date(expiresAt);
  }, []);

  // 设置axios默认配置
  useEffect(() => {
    console.log('AuthProvider useEffect: Initializing...');
    console.log('AuthProvider useEffect: localStorage token:', localStorage.getItem('token'));
    console.log('AuthProvider useEffect: localStorage user:', localStorage.getItem('user'));
    console.log('AuthProvider useEffect: localStorage token_expires:', localStorage.getItem('token_expires'));
    
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && !isTokenExpired()) {
      console.log('AuthProvider useEffect: Token exists and not expired, setting axios header');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // 如果有存储的用户信息，先设置用户状态
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          console.log('AuthProvider useEffect: Setting user from localStorage:', userData);
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('AuthProvider useEffect: Error parsing stored user:', error);
        }
      }
      
      // 验证token有效性
      checkAuth();
    } else {
      console.log('AuthProvider useEffect: Token expired or not exists');
      // token过期或不存在，清理数据
      if (token && isTokenExpired()) {
        console.log('AuthProvider useEffect: Token expired, logging out');
        logout();
      }
      setLoading(false);
    }
  }, [checkAuth, isTokenExpired, logout]);

  const login = async (email, password) => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '' : '';
      const response = await axios.post(`${baseURL}/api/auth/login`, {
        email,
        password
      });
      const { token, user: userData, expires_at, password_change_required, mfa_required, mfa_method, mfa_token } = response.data.data;
      
      if (mfa_required) {
        // 触发MFA二次验证流程，不设置令牌
        setMfaChallenge({ method: mfa_method, token: mfa_token });
        return {
          success: false,
          mfaRequired: true,
          method: mfa_method,
          mfaToken: mfa_token
        };
      }
      
      // 存储token和过期时间
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token_expires', expires_at);
      localStorage.setItem('session_started_at', String(Date.now()));
      localStorage.setItem('session_last_activity_at', String(Date.now()));
      
      // 设置axios默认header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { 
        success: true, 
        passwordChangeRequired: password_change_required 
      };
    } catch (error) {
      console.error('Login failed:', error);
      // 优先使用error字段，其次使用message字段
      throw new Error(error.response?.data?.error || error.response?.data?.message || '登录失败');
    }
  };

  const verifyMFA = async (code) => {
    if (!mfaChallenge?.token) throw new Error('MFA会话不存在');
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '' : '';
      const resp = await axios.post(`${baseURL}/api/auth/mfa/verify`, {
        mfa_token: mfaChallenge.token,
        code
      });
      const { token, user: userData, expires_at, password_change_required } = resp.data.data;
      // 设置令牌
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token_expires', expires_at);
      localStorage.setItem('session_started_at', String(Date.now()));
      localStorage.setItem('session_last_activity_at', String(Date.now()));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      setIsAuthenticated(true);
      setMfaChallenge(null);
      return { success: true, passwordChangeRequired: password_change_required };
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'MFA验证失败');
    }
  };

  const requestMFALoginOTP = async () => {
    if (!mfaChallenge?.token) return;
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '' : '';
      await axios.post(`${baseURL}/api/auth/mfa/request`, {
        mfa_token: mfaChallenge.token
      });
    } catch (error) {
      // 静默失败即可
      console.warn('Request MFA OTP failed:', error);
    }
  };

  const updateUser = async (userData) => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '' : '';
      const response = await axios.put(`${baseURL}/api/auth/profile`, userData);
      const updatedUser = response.data.data;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Update user failed:', error);
      throw new Error(error.response?.data?.message || '更新用户信息失败');
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
    checkAuth,
    mfaChallenge,
    verifyMFA,
    requestMFALoginOTP,
    // 检查用户是否需要强制修改密码
    isPasswordChangeRequired: () => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('force') === 'true' || user?.password_change_required === true;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      clearSessionTimers();
      return undefined;
    }

    if (!localStorage.getItem('session_started_at')) {
      localStorage.setItem('session_started_at', String(Date.now()));
    }
    resetIdleTimer();
    scheduleAbsoluteTimer();

    const activityHandler = () => {
      resetIdleTimer();
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((eventName) => window.addEventListener(eventName, activityHandler, true));

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, activityHandler, true));
      clearSessionTimers();
    };
  }, [clearSessionTimers, isAuthenticated, resetIdleTimer, scheduleAbsoluteTimer]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
